/* =========================================================================
 * SCHOOL × SPORT → CONFERENCE. The association that makes a championship
 * assignable.
 *
 * Brian: "so it looks like every single school and sport needs association."
 *
 * ⚠️ HE IS RIGHT, AND IT IS NOT school → conference. IT IS school × SPORT →
 * conference. That distinction is the whole model, and getting it wrong is what
 * produced most of the phantoms this project has spent days retiring.
 *
 * A school does not belong to one conference. It belongs to a conference IN
 * EACH SPORT, and the answer differs by sport in the same season:
 *
 *   Ohio State      hockey        → Big Ten          (the Big Ten crowns it)
 *   Penn State      men's volley  → EIVA             (the Big Ten does NOT)
 *   Stanford        water polo    → MPSF             (the ACC does NOT)
 *   Notre Dame      football      → INDEPENDENT      (the ACC does NOT)
 *   Navy            football      → American         (the Patriot League does NOT)
 *   Army            football      → American         (the Patriot League does NOT)
 *   Johns Hopkins   lacrosse      → Big Ten          (a DIII school in a DI league)
 *
 * Every one of those is a school whose ring order exists — and every one of
 * them would be filed under the wrong governing body by a school→conference
 * model. Johns Hopkins is the sharpest case: a Division III school that wins a
 * DIVISION I Big Ten championship in one sport. No school-level field can
 * express that.
 *
 * ⚠️ WHY THIS IS THE FIX FOR THE PHANTOMS, NOT A SEPARATE PROBLEM. Every phantom
 * retired this week was the same error read from the other direction: we
 * recorded a Big Ten water polo championship because Big Ten schools play water
 * polo. They do — in the MPSF. "This school plays this sport" and "this
 * conference crowns this sport" are different statements, and the thing that
 * connects them is this table.
 *
 * ⚠️ THE SHAPE IS DEFAULT-PLUS-EXCEPTIONS, DELIBERATELY. A school × sport matrix
 * for ~1,100 schools across ~25 sports is ~27,000 cells, nearly all of which say
 * the same thing: the school's primary conference. Enumerating them all would be
 * 27,000 chances to be wrong and an unmaintainable file. So a school declares
 * ONE primary conference, and only the sports that DEPART from it are listed.
 * The exceptions are also exactly where the money and the errors are.
 * ========================================================================= */

const L = (v) => String(v ?? '').trim().toLowerCase()

/** Not every sport is played in a conference at all. */
export const INDEPENDENT = 'Independent'

/**
 * A school: its primary conference, and the sports that go elsewhere.
 *
 * `except` maps a SPORT to the conference that actually crowns it for this
 * school — or to INDEPENDENT where nobody does.
 */
const A = (name, state, primary, except = {}) => ({ name, state, primary, except })

export const SCHOOL_AFFILIATION = [
  /* ⚠️ GENERATED — edit `tools/gen-affiliation.mjs`, not this list.
   *
   * Brian: "its going to take time but must be done so we can actually use
   * this." He is right, and the first pass at this file was 24 schools written
   * by hand, which is not a foundation.
   *
   * THE INVERSION THAT MADE IT TRACTABLE: a school × sport matrix is ~27,000
   * cells, but every MEMBER of a sport-specific body — the EIWA, the EIVA,
   * Hockey East, the MVFC — is BY DEFINITION a school playing that sport
   * outside its primary conference. So those rosters ARE the exception table.
   * Collecting fifteen of them replaced enumerating twenty-seven thousand
   * cells, and it is why this is 875 schools rather than 24.
   *
   * ⚠️ AND THE TWO HALVES CROSS-CHECK EACH OTHER. A school appears in the
   * departure rosters AND in its own conference's member roster, so the
   * generator compares them and REFUSES rather than preferring either. That
   * caught DAYTON, annotated Horizon League in the Pioneer Football roster when
   * it is Atlantic 10 — a silent preference would have filed every Dayton
   * championship except football under the wrong conference.
   *
   * ⚠️ AND ONE SCHOOL, TWO NAMES, IS ITS OWN SILENT FAILURE. Hockey East lists
   * "Connecticut"; the Big East lists "UConn". Without an alias the generator
   * emitted BOTH rows and `conferenceFor` answered "Big East" for UConn ice
   * hockey — it is Hockey East. A confidently wrong answer produced by a
   * duplicate nobody would spot in a 237-row list. Same for UMass.
   *
   * ⚠️ A CORRECTION THE ROSTERS FORCED: my hand-written version had Cornell,
   * Harvard, Princeton, Columbia and Penn wrestling in the EIWA. THE IVY LEAGUE
   * LEFT THE EIWA IN DECEMBER 2023. Five wrong departures, written from memory,
   * caught only because the real roster was read. That is the whole argument
   * for generating this from sources rather than typing it.
   *
   * ⚠️ WHAT IS DELIBERATELY ABSENT: the full MPSF and CWPA water-polo rosters,
   * which could not be extracted cleanly and were flagged low-confidence. Only
   * the four unambiguous members are here — the exact schools whose Big Ten and
   * ACC water polo championships we retired as phantoms. The rest stay
   * unsourced, because `conferenceFor` returning null is honest and returning a
   * guess is not.
   */
  A('Abilene Christian', 'TX', 'United Athletic Conference', {  }),
  A('Adams State', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('Adrian', 'MI', 'Michigan Intercollegiate Athletic Association', {  }),
  A('Agnes Scott', 'GA', 'Collegiate Conference of the South', {  }),
  A('Air Force', 'CO', 'Mountain West Conference', { 'Ice Hockey': 'Atlantic Hockey America', 'Wrestling': 'Big 12 Conference' }),
  A('Akron', 'OH', 'Mid-American Conference', {  }),
  A('Alabama', 'AL', 'Southeastern Conference', {  }),
  A('Alabama A&M', 'AL', 'Southwestern Athletic Conference', {  }),
  A('Alabama State', 'AL', 'Southwestern Athletic Conference', {  }),
  A('Albany', 'NY', 'America East Conference', { 'Football': 'CAA Football' }),
  A('Albertus Magnus', 'CT', 'Great Northeast Athletic Conference', {  }),
  A('Albion', 'MI', 'Michigan Intercollegiate Athletic Association', {  }),
  A('Albright', 'PA', 'MAC Freedom', {  }),
  A('Alcorn State', 'MS', 'Southwestern Athletic Conference', {  }),
  A('Alfred', 'NY', 'Empire 8 Conference', {  }),
  A('Alfred State', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Allegheny', 'PA', 'North Coast Athletic Conference', {  }),
  A('Alma', 'MI', 'Michigan Intercollegiate Athletic Association', {  }),
  A('Alvernia', 'PA', 'MAC Commonwealth', {  }),
  A('American', 'DC', 'Patriot League', { 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Amherst', 'MA', 'New England Small College Athletic Conference', {  }),
  A('Anderson', 'SC', 'South Atlantic Conference', {  }),
  A('Anderson (IN)', 'IN', 'Heartland Collegiate Athletic Conference', {  }),
  A('Angelo State', 'TX', 'Lone Star Conference', {  }),
  A('Anna Maria', 'MA', 'Great Northeast Athletic Conference', {  }),
  A('Appalachian State', 'NC', 'Sun Belt Conference', {  }),
  A('Arcadia', 'PA', 'MAC Commonwealth', {  }),
  A('Arizona', 'AZ', 'Big 12 Conference', {  }),
  A('Arizona State', 'AZ', 'Big 12 Conference', { 'Ice Hockey': 'National Collegiate Hockey Conference' }),
  A('Arkansas', 'AR', 'Southeastern Conference', {  }),
  A('Arkansas State', 'AR', 'Sun Belt Conference', {  }),
  A('Arkansas-Pine Bluff', 'AR', 'Southwestern Athletic Conference', {  }),
  A('Army', 'NY', 'Patriot League', { 'Football': 'American Conference', 'Ice Hockey': 'Atlantic Hockey America' }),
  A('Asbury', 'KY', 'Collegiate Conference of the South', {  }),
  A('Auburn', 'AL', 'Southeastern Conference', {  }),
  A('Auburn-Montgomery', 'AL', 'Gulf South Conference', {  }),
  A('Augsburg', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('Augusta', 'GA', 'Peach Belt Conference', {  }),
  A('Augustana (IL)', 'IL', 'College Conference of Illinois and Wisconsin', {  }),
  A('Augustana (SD)', 'SD', 'Northern Sun Intercollegiate Conference', { 'Ice Hockey': 'Central Collegiate Hockey Association' }),
  A('Aurora', 'IL', 'Northern Athletics Collegiate Conference', {  }),
  A('Austin College', 'TX', 'Southern Collegiate Athletic Conference', {  }),
  A('Austin Peay', 'TN', 'United Athletic Conference', {  }),
  A('Averett', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Azusa Pacific', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Babson', 'MA', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('Baldwin Wallace', 'OH', 'Ohio Athletic Conference', {  }),
  A('Ball State', 'IN', 'Mid-American Conference', { 'Volleyball (men)': 'Midwestern Intercollegiate Volleyball Association' }),
  A('Bard', 'NY', 'Liberty League', {  }),
  A('Barry', 'FL', 'Sunshine State Conference', {  }),
  A('Baruch', 'NY', 'City University of New York Athletic Conference', {  }),
  A('Bates', 'ME', 'New England Small College Athletic Conference', {  }),
  A('Baylor', 'TX', 'Big 12 Conference', {  }),
  A('Belhaven', 'MS', 'Collegiate Conference of the South', {  }),
  A('Bellarmine', 'KY', 'ASUN Conference', {  }),
  A('Belmont', 'TN', 'Missouri Valley Conference', {  }),
  A('Beloit', 'WI', 'Midwest Conference', {  }),
  A('Bemidji State', 'MN', 'Northern Sun Intercollegiate Conference', { 'Ice Hockey': 'Western Collegiate Hockey Association' }),
  A('Benedictine', 'IL', 'Northern Athletics Collegiate Conference', {  }),
  A('Bentley', 'MA', 'Northeast Conference', { 'Ice Hockey': 'Atlantic Hockey America' }),
  A('Berry', 'GA', 'Southern Athletic Association', {  }),
  A('Bethany (WV)', 'WV', 'Presidents\' Athletic Conference', {  }),
  A('Bethany Lutheran', 'MN', 'Upper Midwest Athletic Conference', {  }),
  A('Bethel', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('Bethune-Cookman', 'FL', 'Southwestern Athletic Conference', {  }),
  A('Binghamton', 'NY', 'America East Conference', { 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Black Hills State', 'SD', 'Rocky Mountain Athletic Conference', {  }),
  A('Blackburn', 'IL', 'St. Louis Intercollegiate Athletic Conference', {  }),
  A('Bloomsburg', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Bluefield State', 'WV', 'Central Intercollegiate Athletic Association', {  }),
  A('Bluffton', 'OH', 'Heartland Collegiate Athletic Conference', {  }),
  A('Boston College', 'MA', 'Atlantic Coast Conference', { 'Ice Hockey': 'Hockey East' }),
  A('Boston University', 'MA', 'Patriot League', { 'Ice Hockey': 'Hockey East' }),
  A('Bowdoin', 'ME', 'New England Small College Athletic Conference', {  }),
  A('Bowie State', 'MD', 'Central Intercollegiate Athletic Association', {  }),
  A('Bowling Green', 'OH', 'Mid-American Conference', { 'Ice Hockey': 'Central Collegiate Hockey Association' }),
  A('Bradley', 'IL', 'Missouri Valley Conference', {  }),
  A('Brandeis', 'MA', 'University Athletic Association', {  }),
  A('Brevard', 'NC', 'USA South Athletic Conference', {  }),
  A('Bridgewater', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Bridgewater State', 'MA', 'Massachusetts State Collegiate Athletic Conference', {  }),
  A('Brockport', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Brooklyn College', 'NY', 'City University of New York Athletic Conference', {  }),
  A('Brown', 'RI', 'Ivy League', { 'Ice Hockey': 'ECAC Hockey' }),
  A('Bryant', 'RI', 'America East Conference', { 'Football': 'CAA Football' }),
  A('Bryn Mawr', 'PA', 'Centennial Conference', {  }),
  A('Bucknell', 'PA', 'Patriot League', { 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Buena Vista', 'IA', 'American Rivers Conference', {  }),
  A('Buffalo', 'NY', 'Mid-American Conference', {  }),
  A('Buffalo State', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Butler', 'IN', 'Big East Conference', { 'Football': 'Pioneer Football League' }),
  A('BYU', 'UT', 'Big 12 Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation' }),
  A('Cairn', 'PA', 'United East Conference', {  }),
  A('Cal Baptist', 'CA', 'Big West Conference', { 'Wrestling': 'Big 12 Conference' }),
  A('Cal Poly', 'CA', 'Big West Conference', {  }),
  A('Cal State Bakersfield', 'CA', 'Big West Conference', {  }),
  A('Cal State Fullerton', 'CA', 'Big West Conference', {  }),
  A('California', 'CA', 'Atlantic Coast Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation', 'Water Polo': 'Mountain Pacific Sports Federation' }),
  A('California (PA)', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('California Lutheran', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Caltech', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Calvin', 'MI', 'Michigan Intercollegiate Athletic Association', {  }),
  A('Cameron', 'OK', 'Lone Star Conference', {  }),
  A('Campbell', 'NC', 'Coastal Athletic Association', {  }),
  A('Canisius', 'NY', 'Metro Atlantic Athletic Conference', { 'Ice Hockey': 'Atlantic Hockey America' }),
  A('Canton', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Capital', 'OH', 'Ohio Athletic Conference', {  }),
  A('Carleton', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('Carlow', 'PA', 'Allegheny Mountain Collegiate Conference', {  }),
  A('Carnegie Mellon', 'PA', 'University Athletic Association', {  }),
  A('Carroll', 'WI', 'College Conference of Illinois and Wisconsin', {  }),
  A('Carson-Newman', 'TN', 'South Atlantic Conference', {  }),
  A('Carthage', 'WI', 'College Conference of Illinois and Wisconsin', {  }),
  A('Case Western Reserve', 'OH', 'University Athletic Association', {  }),
  A('Catawba', 'NC', 'South Atlantic Conference', {  }),
  A('Catholic', 'DC', 'Landmark Conference', {  }),
  A('Cedar Crest', 'PA', 'United East Conference', {  }),
  A('Centenary (LA)', 'LA', 'Southern Collegiate Athletic Conference', {  }),
  A('Centenary (NJ)', 'NJ', 'Atlantic East Conference', {  }),
  A('Central', 'IA', 'American Rivers Conference', {  }),
  A('Central Arkansas', 'AR', 'United Athletic Conference', {  }),
  A('Central Connecticut', 'CT', 'Northeast Conference', {  }),
  A('Central Michigan', 'MI', 'Mid-American Conference', {  }),
  A('Centre', 'KY', 'Southern Athletic Association', {  }),
  A('Chadron State', 'NE', 'Rocky Mountain Athletic Conference', {  }),
  A('Chapman', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Charleston', 'SC', 'Coastal Athletic Association', {  }),
  A('Charleston (WV)', 'WV', 'Mountain East Conference', { 'Volleyball (men)': 'Eastern Intercollegiate Volleyball Association' }),
  A('Charleston Southern', 'SC', 'Big South Conference', {  }),
  A('Charlotte', 'NC', 'American Conference', {  }),
  A('Chatham', 'PA', 'Presidents\' Athletic Conference', {  }),
  A('Chattanooga', 'TN', 'Southern Conference', {  }),
  A('Chicago', 'IL', 'University Athletic Association', {  }),
  A('Chicago State', 'IL', 'Northeast Conference', {  }),
  A('Christian Brothers', 'TN', 'Gulf South Conference', {  }),
  A('Christopher Newport', 'VA', 'Coast to Coast Athletic Conference', {  }),
  A('Cincinnati', 'OH', 'Big 12 Conference', {  }),
  A('Citadel', 'SC', 'Southern Conference', {  }),
  A('City College', 'NY', 'City University of New York Athletic Conference', {  }),
  A('Claflin', 'SC', 'Central Intercollegiate Athletic Association', {  }),
  A('Claremont-Mudd-Scripps', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Clarion', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Clark', 'MA', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('Clarkson', 'NY', 'Liberty League', { 'Ice Hockey': 'ECAC Hockey' }),
  A('Clayton State', 'GA', 'Peach Belt Conference', {  }),
  A('Clemson', 'SC', 'Atlantic Coast Conference', {  }),
  A('Cleveland State', 'OH', 'Horizon League', {  }),
  A('Coastal Carolina', 'SC', 'Sun Belt Conference', {  }),
  A('Cobleskill', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Coe', 'IA', 'American Rivers Conference', {  }),
  A('Coker', 'SC', 'South Atlantic Conference', {  }),
  A('Colby', 'ME', 'New England Small College Athletic Conference', {  }),
  A('Colby-Sawyer', 'NH', 'Great Northeast Athletic Conference', {  }),
  A('Colgate', 'NY', 'Patriot League', { 'Ice Hockey': 'ECAC Hockey' }),
  A('College of Staten Island', 'NY', 'City University of New York Athletic Conference', {  }),
  A('Colorado', 'CO', 'Big 12 Conference', {  }),
  A('Colorado Christian', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('Colorado College', 'CO', 'Southern Collegiate Athletic Conference', { 'Ice Hockey': 'National Collegiate Hockey Conference' }),
  A('Colorado Mesa', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('Colorado Mines', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('Columbia', 'NY', 'Ivy League', {  }),
  A('Columbus State', 'GA', 'Peach Belt Conference', {  }),
  A('Concordia (WI)', 'WI', 'Northern Athletics Collegiate Conference', {  }),
  A('Concordia Texas', 'TX', 'Southern Collegiate Athletic Conference', {  }),
  A('Concordia-Moorhead', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('Concordia-St. Paul', 'MN', 'Northern Sun Intercollegiate Conference', {  }),
  A('Connecticut College', 'CT', 'New England Small College Athletic Conference', {  }),
  A('Coppin State', 'MD', 'Mid-Eastern Athletic Conference', {  }),
  A('Cornell', 'NY', 'Ivy League', { 'Ice Hockey': 'ECAC Hockey' }),
  A('Cornell College', 'IA', 'Midwest Conference', {  }),
  A('Cortland', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Covenant', 'GA', 'Collegiate Conference of the South', {  }),
  A('Creighton', 'NE', 'Big East Conference', {  }),
  A('Crown', 'MN', 'Upper Midwest Athletic Conference', {  }),
  A('CSU Pueblo', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('CSUN', 'CA', 'Big West Conference', {  }),
  A('Curry', 'MA', 'Conference of New England', {  }),
  A('Dallas', 'TX', 'Southern Collegiate Athletic Conference', {  }),
  A('Dallas Baptist', 'TX', 'Lone Star Conference', {  }),
  A('Dartmouth', 'NH', 'Ivy League', { 'Ice Hockey': 'ECAC Hockey' }),
  A('Davidson', 'NC', 'Atlantic 10 Conference', { 'Football': 'Pioneer Football League' }),
  A('Dayton', 'OH', 'Atlantic 10 Conference', { 'Football': 'Pioneer Football League' }),
  A('Dean', 'MA', 'Great Northeast Athletic Conference', {  }),
  A('Defiance', 'OH', 'Heartland Collegiate Athletic Conference', {  }),
  A('Delaware', 'DE', 'Conference USA', { 'Ice Hockey': 'Atlantic Hockey America' }),
  A('Delaware State', 'DE', 'Mid-Eastern Athletic Conference', {  }),
  A('Delaware Valley', 'PA', 'MAC Freedom', {  }),
  A('Delhi', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Delta State', 'MS', 'Gulf South Conference', {  }),
  A('Denison', 'OH', 'North Coast Athletic Conference', {  }),
  A('Denver', 'CO', 'Summit League', { 'Ice Hockey': 'National Collegiate Hockey Conference' }),
  A('DePaul', 'IL', 'Big East Conference', {  }),
  A('DePauw', 'IN', 'North Coast Athletic Conference', {  }),
  A('DeSales', 'PA', 'MAC Commonwealth', {  }),
  A('Detroit Mercy', 'MI', 'Horizon League', {  }),
  A('Dickinson', 'PA', 'Centennial Conference', {  }),
  A('Dominican (IL)', 'IL', 'Northern Athletics Collegiate Conference', {  }),
  A('Drake', 'IA', 'Missouri Valley Conference', { 'Football': 'Pioneer Football League' }),
  A('Drew', 'NJ', 'Landmark Conference', {  }),
  A('Drexel', 'PA', 'Coastal Athletic Association', { 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Drury', 'MO', 'Great Lakes Valley Conference', {  }),
  A('Dubuque', 'IA', 'American Rivers Conference', {  }),
  A('Duke', 'NC', 'Atlantic Coast Conference', {  }),
  A('Duquesne', 'PA', 'Atlantic 10 Conference', {  }),
  A('East Carolina', 'NC', 'American Conference', {  }),
  A('East Stroudsburg', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('East Tennessee State', 'TN', 'Southern Conference', {  }),
  A('East Texas A&M', 'TX', 'Southland Conference', {  }),
  A('East Texas Baptist', 'TX', 'American Southwest Conference', {  }),
  A('Eastern', 'PA', 'MAC Commonwealth', {  }),
  A('Eastern Connecticut', 'CT', 'Little East Conference', {  }),
  A('Eastern Illinois', 'IL', 'Ohio Valley Conference', {  }),
  A('Eastern Kentucky', 'KY', 'United Athletic Conference', {  }),
  A('Eastern Mennonite', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Eastern Michigan', 'MI', 'Mid-American Conference', {  }),
  A('Eastern New Mexico', 'NM', 'Lone Star Conference', {  }),
  A('Eastern Washington', 'WA', 'Big Sky Conference', {  }),
  A('Eckerd', 'FL', 'Sunshine State Conference', {  }),
  A('Edgewood', 'WI', 'Northern Athletics Collegiate Conference', {  }),
  A('Edinboro', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Elizabeth City State', 'NC', 'Central Intercollegiate Athletic Association', {  }),
  A('Elizabethtown', 'PA', 'Landmark Conference', {  }),
  A('Elmhurst', 'IL', 'College Conference of Illinois and Wisconsin', {  }),
  A('Elmira', 'NY', 'Empire 8 Conference', {  }),
  A('Elms', 'MA', 'Great Northeast Athletic Conference', {  }),
  A('Elon', 'NC', 'Coastal Athletic Association', {  }),
  A('Embry-Riddle', 'FL', 'Sunshine State Conference', {  }),
  A('Emerson', 'MA', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('Emmanuel', 'MA', 'Great Northeast Athletic Conference', {  }),
  A('Emory', 'GA', 'University Athletic Association', {  }),
  A('Emory & Henry', 'VA', 'South Atlantic Conference', {  }),
  A('Endicott', 'MA', 'Conference of New England', {  }),
  A('Eureka', 'IL', 'St. Louis Intercollegiate Athletic Conference', {  }),
  A('Evansville', 'IN', 'Missouri Valley Conference', {  }),
  A('Fairfield', 'CT', 'Metro Atlantic Athletic Conference', {  }),
  A('Fairleigh Dickinson', 'NJ', 'Northeast Conference', {  }),
  A('Fayetteville State', 'NC', 'Central Intercollegiate Athletic Association', {  }),
  A('FDU-Florham', 'NJ', 'MAC Commonwealth', {  }),
  A('Ferris State', 'MI', 'Great Lakes Intercollegiate Athletic Conference', { 'Ice Hockey': 'Central Collegiate Hockey Association' }),
  A('Ferrum', 'VA', 'USA South Athletic Conference', {  }),
  A('FGCU', 'FL', 'ASUN Conference', {  }),
  A('Fitchburg State', 'MA', 'Massachusetts State Collegiate Athletic Conference', {  }),
  A('Flagler', 'FL', 'Peach Belt Conference', {  }),
  A('Florida', 'FL', 'Southeastern Conference', {  }),
  A('Florida A&M', 'FL', 'Southwestern Athletic Conference', {  }),
  A('Florida Atlantic', 'FL', 'American Conference', {  }),
  A('Florida Southern', 'FL', 'Sunshine State Conference', {  }),
  A('Florida State', 'FL', 'Atlantic Coast Conference', {  }),
  A('Florida Tech', 'FL', 'Sunshine State Conference', {  }),
  A('Fontbonne', 'MO', 'St. Louis Intercollegiate Athletic Conference', {  }),
  A('Fordham', 'NY', 'Atlantic 10 Conference', {  }),
  A('Fort Lewis', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('Framingham State', 'MA', 'Massachusetts State Collegiate Athletic Conference', {  }),
  A('Franklin', 'IN', 'Heartland Collegiate Athletic Conference', {  }),
  A('Franklin & Marshall', 'PA', 'Centennial Conference', { 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Fredonia', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Furman', 'SC', 'Southern Conference', {  }),
  A('Gallaudet', 'DC', 'United East Conference', {  }),
  A('Gannon', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Gardner-Webb', 'NC', 'Big South Conference', {  }),
  A('Geneseo', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Geneva', 'PA', 'Presidents\' Athletic Conference', {  }),
  A('George Fox', 'OR', 'Northwest Conference', {  }),
  A('George Mason', 'VA', 'Atlantic 10 Conference', { 'Volleyball (men)': 'Eastern Intercollegiate Volleyball Association', 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('George Washington', 'DC', 'Atlantic 10 Conference', {  }),
  A('Georgetown', 'DC', 'Big East Conference', {  }),
  A('Georgia', 'GA', 'Southeastern Conference', {  }),
  A('Georgia College', 'GA', 'Peach Belt Conference', {  }),
  A('Georgia Southern', 'GA', 'Sun Belt Conference', {  }),
  A('Georgia Southwestern', 'GA', 'Peach Belt Conference', {  }),
  A('Georgia State', 'GA', 'Sun Belt Conference', {  }),
  A('Georgia Tech', 'GA', 'Atlantic Coast Conference', {  }),
  A('Gettysburg', 'PA', 'Centennial Conference', {  }),
  A('Gordon', 'MA', 'Conference of New England', {  }),
  A('Goucher', 'MD', 'Landmark Conference', {  }),
  A('Grambling State', 'LA', 'Southwestern Athletic Conference', {  }),
  A('Green Bay', 'WI', 'Horizon League', {  }),
  A('Greensboro', 'NC', 'USA South Athletic Conference', {  }),
  A('Greenville', 'IL', 'St. Louis Intercollegiate Athletic Conference', {  }),
  A('Grinnell', 'IA', 'Midwest Conference', {  }),
  A('Grove City', 'PA', 'Presidents\' Athletic Conference', {  }),
  A('Guilford', 'NC', 'Old Dominion Athletic Conference', {  }),
  A('Gustavus Adolphus', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('Gwynedd Mercy', 'PA', 'Atlantic East Conference', {  }),
  A('Hamilton', 'NY', 'New England Small College Athletic Conference', {  }),
  A('Hamline', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('Hampden-Sydney', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Hampton', 'VA', 'Coastal Athletic Association', {  }),
  A('Hanover', 'IN', 'Heartland Collegiate Athletic Conference', {  }),
  A('Hardin-Simmons', 'TX', 'American Southwest Conference', {  }),
  A('Hartford', 'CT', 'Conference of New England', {  }),
  A('Hartwick', 'NY', 'Empire 8 Conference', {  }),
  A('Harvard', 'MA', 'Ivy League', { 'Ice Hockey': 'ECAC Hockey', 'Volleyball (men)': 'Eastern Intercollegiate Volleyball Association' }),
  A('Haverford', 'PA', 'Centennial Conference', {  }),
  A('Heidelberg', 'OH', 'Ohio Athletic Conference', {  }),
  A('Hendrix', 'AR', 'Southern Collegiate Athletic Conference', {  }),
  A('High Point', 'NC', 'Big South Conference', {  }),
  A('Hilbert', 'NY', 'Allegheny Mountain Collegiate Conference', {  }),
  A('Hiram', 'OH', 'North Coast Athletic Conference', {  }),
  A('Hobart and William Smith', 'NY', 'Liberty League', {  }),
  A('Hofstra', 'NY', 'Coastal Athletic Association', { 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Hollins', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Holy Cross', 'MA', 'Patriot League', { 'Ice Hockey': 'Hockey East' }),
  A('Hope', 'MI', 'Michigan Intercollegiate Athletic Association', {  }),
  A('Houston', 'TX', 'Big 12 Conference', {  }),
  A('Houston Christian', 'TX', 'Southland Conference', {  }),
  A('Howard', 'DC', 'Mid-Eastern Athletic Conference', {  }),
  A('Howard Payne', 'TX', 'American Southwest Conference', {  }),
  A('Hunter College', 'NY', 'City University of New York Athletic Conference', {  }),
  A('Huntingdon', 'AL', 'Collegiate Conference of the South', {  }),
  A('Husson', 'ME', 'North Atlantic Conference', {  }),
  A('Idaho', 'ID', 'Big Sky Conference', {  }),
  A('Idaho State', 'ID', 'Big Sky Conference', {  }),
  A('Illinois', 'IL', 'Big Ten Conference', {  }),
  A('Illinois College', 'IL', 'Midwest Conference', {  }),
  A('Illinois State', 'IL', 'Missouri Valley Conference', { 'Football': 'Missouri Valley Football Conference' }),
  A('Illinois Wesleyan', 'IL', 'College Conference of Illinois and Wisconsin', {  }),
  A('Immaculata', 'PA', 'Atlantic East Conference', {  }),
  A('Incarnate Word', 'TX', 'Southland Conference', {  }),
  A('Indiana', 'IN', 'Big Ten Conference', {  }),
  A('Indiana State', 'IN', 'Missouri Valley Conference', { 'Football': 'Missouri Valley Football Conference' }),
  A('Iona', 'NY', 'Metro Atlantic Athletic Conference', {  }),
  A('Iowa', 'IA', 'Big Ten Conference', {  }),
  A('Iowa State', 'IA', 'Big 12 Conference', {  }),
  A('Ithaca', 'NY', 'Liberty League', {  }),
  A('IU Indy', 'IN', 'Horizon League', {  }),
  A('IUP', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Jackson State', 'MS', 'Southwestern Athletic Conference', {  }),
  A('Jacksonville', 'FL', 'ASUN Conference', {  }),
  A('James Madison', 'VA', 'Sun Belt Conference', {  }),
  A('Jamestown', 'ND', 'Northern Sun Intercollegiate Conference', {  }),
  A('John Jay', 'NY', 'City University of New York Athletic Conference', {  }),
  A('Johns Hopkins', 'MD', 'Centennial Conference', { 'Lacrosse': 'Big Ten Conference' }),
  A('Johnson & Wales (RI)', 'RI', 'Conference of New England', {  }),
  A('Johnson & Wales-Charlotte', 'NC', 'Coast to Coast Athletic Conference', {  }),
  A('Johnson C. Smith', 'NC', 'Central Intercollegiate Athletic Association', {  }),
  A('Juniata', 'PA', 'Landmark Conference', {  }),
  A('Kalamazoo', 'MI', 'Michigan Intercollegiate Athletic Association', {  }),
  A('Kansas', 'KS', 'Big 12 Conference', {  }),
  A('Kansas City', 'MO', 'Summit League', {  }),
  A('Kansas State', 'KS', 'Big 12 Conference', {  }),
  A('Kean', 'NJ', 'New Jersey Athletic Conference', {  }),
  A('Keene State', 'NH', 'Little East Conference', {  }),
  A('Kent State', 'OH', 'Mid-American Conference', {  }),
  A('Kentucky', 'KY', 'Southeastern Conference', {  }),
  A('Kenyon', 'OH', 'North Coast Athletic Conference', {  }),
  A('Keystone', 'PA', 'United East Conference', {  }),
  A('King\'s (PA)', 'PA', 'MAC Freedom', {  }),
  A('Knox', 'IL', 'Midwest Conference', {  }),
  A('Kutztown', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('La Roche', 'PA', 'Allegheny Mountain Collegiate Conference', {  }),
  A('La Salle', 'PA', 'Atlantic 10 Conference', {  }),
  A('La Verne', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Lafayette', 'PA', 'Patriot League', {  }),
  A('LaGrange', 'GA', 'Collegiate Conference of the South', {  }),
  A('Lake Forest', 'IL', 'Midwest Conference', {  }),
  A('Lake Superior State', 'MI', 'Great Lakes Intercollegiate Athletic Conference', { 'Ice Hockey': 'Central Collegiate Hockey Association' }),
  A('Lakeland', 'WI', 'Northern Athletics Collegiate Conference', {  }),
  A('Lamar', 'TX', 'Southland Conference', {  }),
  A('Lancaster Bible', 'PA', 'United East Conference', {  }),
  A('Lander', 'SC', 'Peach Belt Conference', {  }),
  A('Lasell', 'MA', 'Great Northeast Athletic Conference', {  }),
  A('Lawrence', 'WI', 'Midwest Conference', {  }),
  A('Le Moyne', 'NY', 'Northeast Conference', {  }),
  A('Lebanon Valley', 'PA', 'MAC Commonwealth', {  }),
  A('Lee', 'TN', 'Gulf South Conference', {  }),
  A('Lehigh', 'PA', 'Patriot League', { 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Lehman College', 'NY', 'City University of New York Athletic Conference', {  }),
  A('Lenoir-Rhyne', 'NC', 'South Atlantic Conference', {  }),
  A('Lesley', 'MA', 'North Atlantic Conference', {  }),
  A('LeTourneau', 'TX', 'Southern Collegiate Athletic Conference', {  }),
  A('Lewis', 'IL', 'Great Lakes Valley Conference', { 'Volleyball (men)': 'Midwestern Intercollegiate Volleyball Association' }),
  A('Lewis & Clark', 'OR', 'Northwest Conference', {  }),
  A('Lincoln', 'MO', 'Great Lakes Valley Conference', {  }),
  A('Lincoln (PA)', 'PA', 'Central Intercollegiate Athletic Association', {  }),
  A('Lincoln Memorial', 'TN', 'South Atlantic Conference', {  }),
  A('Lindenwood', 'MO', 'Ohio Valley Conference', { 'Ice Hockey': 'Atlantic Hockey America', 'Volleyball (men)': 'Midwestern Intercollegiate Volleyball Association' }),
  A('Linfield', 'OR', 'Northwest Conference', {  }),
  A('Lipscomb', 'TN', 'ASUN Conference', {  }),
  A('LIU', 'NY', 'Northeast Conference', {  }),
  A('Livingstone', 'NC', 'Central Intercollegiate Athletic Association', {  }),
  A('Lock Haven', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Long Beach State', 'CA', 'Big West Conference', {  }),
  A('Longwood', 'VA', 'Big South Conference', {  }),
  A('Loras', 'IA', 'American Rivers Conference', {  }),
  A('Louisiana', 'LA', 'Sun Belt Conference', {  }),
  A('Louisiana-Monroe', 'LA', 'Sun Belt Conference', {  }),
  A('Louisville', 'KY', 'Atlantic Coast Conference', {  }),
  A('Loyola Chicago', 'IL', 'Atlantic 10 Conference', { 'Volleyball (men)': 'Midwestern Intercollegiate Volleyball Association' }),
  A('Loyola Maryland', 'MD', 'Patriot League', {  }),
  A('LSU', 'LA', 'Southeastern Conference', {  }),
  A('Lubbock Christian', 'TX', 'Lone Star Conference', {  }),
  A('Luther', 'IA', 'Midwest Conference', {  }),
  A('Lycoming', 'PA', 'Landmark Conference', {  }),
  A('Lynchburg', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Lynn', 'FL', 'Sunshine State Conference', {  }),
  A('Macalester', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('Maine', 'ME', 'America East Conference', { 'Football': 'CAA Football', 'Ice Hockey': 'Hockey East' }),
  A('Maine Maritime', 'ME', 'North Atlantic Conference', {  }),
  A('Maine-Farmington', 'ME', 'North Atlantic Conference', {  }),
  A('Maine-Presque Isle', 'ME', 'North Atlantic Conference', {  }),
  A('Manchester', 'IN', 'Heartland Collegiate Athletic Conference', {  }),
  A('Manhattan', 'NY', 'Metro Atlantic Athletic Conference', {  }),
  A('Manhattanville', 'NY', 'Skyline Conference', {  }),
  A('Mansfield', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Marian (WI)', 'WI', 'Northern Athletics Collegiate Conference', {  }),
  A('Marietta', 'OH', 'Ohio Athletic Conference', {  }),
  A('Marist', 'NY', 'Metro Atlantic Athletic Conference', { 'Football': 'Pioneer Football League' }),
  A('Marquette', 'WI', 'Big East Conference', {  }),
  A('Mars Hill', 'NC', 'South Atlantic Conference', {  }),
  A('Marshall', 'WV', 'Sun Belt Conference', {  }),
  A('Martin Luther', 'MN', 'Upper Midwest Athletic Conference', {  }),
  A('Mary', 'ND', 'Northern Sun Intercollegiate Conference', {  }),
  A('Mary Washington', 'VA', 'Coast to Coast Athletic Conference', {  }),
  A('Maryland', 'MD', 'Big Ten Conference', {  }),
  A('Maryland-Eastern Shore', 'MD', 'Mid-Eastern Athletic Conference', {  }),
  A('Marymount', 'VA', 'Atlantic East Conference', {  }),
  A('Maryville', 'MO', 'Great Lakes Valley Conference', {  }),
  A('Maryville (TN)', 'TN', 'Southern Athletic Association', {  }),
  A('Marywood', 'PA', 'MAC Freedom', {  }),
  A('Mass Maritime', 'MA', 'Little East Conference', {  }),
  A('McDaniel', 'MD', 'Centennial Conference', {  }),
  A('McKendree', 'IL', 'Great Lakes Valley Conference', { 'Volleyball (men)': 'Midwestern Intercollegiate Volleyball Association' }),
  A('MCLA', 'MA', 'Massachusetts State Collegiate Athletic Conference', {  }),
  A('McMurry', 'TX', 'American Southwest Conference', {  }),
  A('McNeese', 'LA', 'Southland Conference', {  }),
  A('Medgar Evers', 'NY', 'City University of New York Athletic Conference', {  }),
  A('Memphis', 'TN', 'American Conference', {  }),
  A('Mercer', 'GA', 'Southern Conference', {  }),
  A('Merchant Marine', 'NY', 'Landmark Conference', {  }),
  A('Mercyhurst', 'PA', 'Northeast Conference', { 'Ice Hockey': 'Atlantic Hockey America' }),
  A('Merrimack', 'MA', 'Metro Atlantic Athletic Conference', { 'Ice Hockey': 'Hockey East' }),
  A('Messiah', 'PA', 'MAC Commonwealth', {  }),
  A('Methodist', 'NC', 'USA South Athletic Conference', {  }),
  A('Miami', 'FL', 'Atlantic Coast Conference', {  }),
  A('Miami (OH)', 'OH', 'Mid-American Conference', { 'Ice Hockey': 'National Collegiate Hockey Conference' }),
  A('Michigan', 'MI', 'Big Ten Conference', {  }),
  A('Michigan State', 'MI', 'Big Ten Conference', {  }),
  A('Michigan Tech', 'MI', 'Great Lakes Intercollegiate Athletic Conference', { 'Ice Hockey': 'Central Collegiate Hockey Association' }),
  A('Middle Georgia State', 'GA', 'Peach Belt Conference', {  }),
  A('Middlebury', 'VT', 'New England Small College Athletic Conference', {  }),
  A('Midwestern State', 'TX', 'Lone Star Conference', {  }),
  A('Millersville', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Millsaps', 'MS', 'Southern Athletic Association', {  }),
  A('Milwaukee', 'WI', 'Horizon League', {  }),
  A('Minnesota', 'MN', 'Big Ten Conference', { 'Ice Hockey': 'Western Collegiate Hockey Association' }),
  A('Minnesota Duluth', 'MN', 'Northern Sun Intercollegiate Conference', { 'Ice Hockey': 'Western Collegiate Hockey Association' }),
  A('Minnesota Morris', 'MN', 'Upper Midwest Athletic Conference', {  }),
  A('Minnesota State', 'MN', 'Northern Sun Intercollegiate Conference', { 'Ice Hockey': 'Western Collegiate Hockey Association' }),
  A('Minnesota-Crookston', 'MN', 'Northern Sun Intercollegiate Conference', {  }),
  A('Minot State', 'ND', 'Northern Sun Intercollegiate Conference', {  }),
  A('Misericordia', 'PA', 'MAC Freedom', {  }),
  A('Mississippi Christian', 'MS', 'Gulf South Conference', {  }),
  A('Mississippi State', 'MS', 'Southeastern Conference', {  }),
  A('Mississippi Valley State', 'MS', 'Southwestern Athletic Conference', {  }),
  A('Missouri', 'MO', 'Southeastern Conference', { 'Wrestling': 'Big 12 Conference' }),
  A('Missouri S&T', 'MO', 'Great Lakes Valley Conference', {  }),
  A('MIT', 'MA', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('Monmouth', 'NJ', 'Coastal Athletic Association', {  }),
  A('Monmouth (IL)', 'IL', 'Midwest Conference', {  }),
  A('Montana', 'MT', 'Big Sky Conference', {  }),
  A('Montana State', 'MT', 'Big Sky Conference', {  }),
  A('Montclair State', 'NJ', 'New Jersey Athletic Conference', {  }),
  A('Montevallo', 'AL', 'Gulf South Conference', {  }),
  A('Moravian', 'PA', 'Landmark Conference', {  }),
  A('Morehead State', 'KY', 'Ohio Valley Conference', { 'Football': 'Pioneer Football League' }),
  A('Morgan State', 'MD', 'Mid-Eastern Athletic Conference', { 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Morrisville', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Mount Aloysius', 'PA', 'Allegheny Mountain Collegiate Conference', {  }),
  A('Mount Holyoke', 'MA', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('Mount Saint Mary (NY)', 'NY', 'Skyline Conference', {  }),
  A('Mount Saint Vincent', 'NY', 'Skyline Conference', {  }),
  A('Mount St. Joseph', 'OH', 'Heartland Collegiate Athletic Conference', {  }),
  A('Mount St. Mary\'s', 'MD', 'Metro Atlantic Athletic Conference', {  }),
  A('Mount Union', 'OH', 'Ohio Athletic Conference', {  }),
  A('MSU Denver', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('MSU-Moorhead', 'MN', 'Northern Sun Intercollegiate Conference', {  }),
  A('Muhlenberg', 'PA', 'Centennial Conference', {  }),
  A('Murray State', 'KY', 'Missouri Valley Conference', { 'Football': 'Missouri Valley Football Conference' }),
  A('Muskingum', 'OH', 'Ohio Athletic Conference', {  }),
  A('Navy', 'MD', 'Patriot League', { 'Football': 'American Conference', 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Nazareth', 'NY', 'Empire 8 Conference', {  }),
  A('NC State', 'NC', 'Atlantic Coast Conference', {  }),
  A('Nebraska', 'NE', 'Big Ten Conference', {  }),
  A('Nebraska Omaha', 'NE', 'Summit League', { 'Ice Hockey': 'National Collegiate Hockey Conference' }),
  A('Nebraska Wesleyan', 'NE', 'American Rivers Conference', {  }),
  A('Neumann', 'PA', 'MAC Commonwealth', {  }),
  A('New England (ME)', 'ME', 'Conference of New England', {  }),
  A('New England College', 'NH', 'Great Northeast Athletic Conference', {  }),
  A('New Hampshire', 'NH', 'America East Conference', { 'Football': 'CAA Football', 'Ice Hockey': 'Hockey East' }),
  A('New Haven', 'CT', 'Northeast Conference', {  }),
  A('New Mexico Highlands', 'NM', 'Rocky Mountain Athletic Conference', {  }),
  A('New Orleans', 'LA', 'Southland Conference', {  }),
  A('Newberry', 'SC', 'South Atlantic Conference', {  }),
  A('Niagara', 'NY', 'Metro Atlantic Athletic Conference', { 'Ice Hockey': 'Atlantic Hockey America' }),
  A('Nicholls', 'LA', 'Southland Conference', {  }),
  A('Nichols', 'MA', 'Conference of New England', {  }),
  A('NJIT', 'NJ', 'America East Conference', { 'Volleyball (men)': 'Eastern Intercollegiate Volleyball Association' }),
  A('Norfolk State', 'VA', 'Mid-Eastern Athletic Conference', {  }),
  A('North Alabama', 'AL', 'United Athletic Conference', {  }),
  A('North Carolina', 'NC', 'Atlantic Coast Conference', {  }),
  A('North Carolina A&T', 'NC', 'Coastal Athletic Association', {  }),
  A('North Carolina Central', 'NC', 'Mid-Eastern Athletic Conference', {  }),
  A('North Carolina Wesleyan', 'NC', 'USA South Athletic Conference', {  }),
  A('North Central', 'IL', 'College Conference of Illinois and Wisconsin', {  }),
  A('North Central (MN)', 'MN', 'Upper Midwest Athletic Conference', {  }),
  A('North Dakota', 'ND', 'Summit League', { 'Football': 'Missouri Valley Football Conference', 'Ice Hockey': 'National Collegiate Hockey Conference' }),
  A('North Dakota State', 'ND', 'Summit League', { 'Wrestling': 'Big 12 Conference' }),
  A('North Florida', 'FL', 'ASUN Conference', {  }),
  A('North Georgia', 'GA', 'Peach Belt Conference', {  }),
  A('North Park', 'IL', 'College Conference of Illinois and Wisconsin', {  }),
  A('North Texas', 'TX', 'American Conference', {  }),
  A('Northeastern', 'MA', 'Coastal Athletic Association', { 'Ice Hockey': 'Hockey East' }),
  A('Northern Arizona', 'AZ', 'Big Sky Conference', {  }),
  A('Northern Colorado', 'CO', 'Big Sky Conference', { 'Wrestling': 'Big 12 Conference' }),
  A('Northern Illinois', 'IL', 'Horizon League', {  }),
  A('Northern Iowa', 'IA', 'Missouri Valley Conference', { 'Football': 'Missouri Valley Football Conference', 'Wrestling': 'Big 12 Conference' }),
  A('Northern Kentucky', 'KY', 'Horizon League', { 'Volleyball (men)': 'Midwestern Intercollegiate Volleyball Association' }),
  A('Northern Michigan', 'MI', 'Great Lakes Intercollegiate Athletic Conference', { 'Ice Hockey': 'Central Collegiate Hockey Association' }),
  A('Northern State', 'SD', 'Northern Sun Intercollegiate Conference', {  }),
  A('Northwestern', 'IL', 'Big Ten Conference', {  }),
  A('Northwestern (MN)', 'MN', 'Upper Midwest Athletic Conference', {  }),
  A('Northwestern State', 'LA', 'Southland Conference', {  }),
  A('Notre Dame', 'IN', 'Atlantic Coast Conference', { 'Football': 'Independent', 'Ice Hockey': 'Big Ten Conference' }),
  A('Notre Dame of Maryland', 'MD', 'United East Conference', {  }),
  A('Nova Southeastern', 'FL', 'Sunshine State Conference', {  }),
  A('NYU', 'NY', 'University Athletic Association', {  }),
  A('Oakland', 'MI', 'Horizon League', {  }),
  A('Oberlin', 'OH', 'North Coast Athletic Conference', {  }),
  A('Occidental', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Oglethorpe', 'GA', 'Southern Athletic Association', {  }),
  A('Ohio', 'OH', 'Mid-American Conference', {  }),
  A('Ohio Northern', 'OH', 'Ohio Athletic Conference', {  }),
  A('Ohio State', 'OH', 'Big Ten Conference', { 'Ice Hockey': 'Western Collegiate Hockey Association', 'Volleyball (men)': 'Midwestern Intercollegiate Volleyball Association' }),
  A('Ohio Wesleyan', 'OH', 'North Coast Athletic Conference', {  }),
  A('Oklahoma', 'OK', 'Southeastern Conference', { 'Wrestling': 'Big 12 Conference' }),
  A('Oklahoma Christian', 'OK', 'Lone Star Conference', {  }),
  A('Oklahoma State', 'OK', 'Big 12 Conference', {  }),
  A('Old Dominion', 'VA', 'Sun Belt Conference', {  }),
  A('Ole Miss', 'MS', 'Southeastern Conference', {  }),
  A('Olivet', 'MI', 'Michigan Intercollegiate Athletic Association', {  }),
  A('Omaha', 'NE', 'Summit League', {  }),
  A('Oneonta', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Oral Roberts', 'OK', 'Summit League', {  }),
  A('Oregon', 'OR', 'Big Ten Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation' }),
  A('Oswego', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Otterbein', 'OH', 'Ohio Athletic Conference', {  }),
  A('Ozarks (AR)', 'AR', 'Southern Collegiate Athletic Conference', {  }),
  A('Pacific (OR)', 'OR', 'Northwest Conference', {  }),
  A('Pacific Lutheran', 'WA', 'Northwest Conference', {  }),
  A('Palm Beach Atlantic', 'FL', 'Sunshine State Conference', {  }),
  A('Penn', 'PA', 'Ivy League', {  }),
  A('Penn College', 'PA', 'United East Conference', {  }),
  A('Penn State', 'PA', 'Big Ten Conference', { 'Volleyball (men)': 'Eastern Intercollegiate Volleyball Association' }),
  A('Penn State-Abington', 'PA', 'United East Conference', {  }),
  A('Penn State-Altoona', 'PA', 'Allegheny Mountain Collegiate Conference', {  }),
  A('Penn State-Behrend', 'PA', 'Allegheny Mountain Collegiate Conference', {  }),
  A('Penn State-Berks', 'PA', 'United East Conference', {  }),
  A('Penn State-Brandywine', 'PA', 'United East Conference', {  }),
  A('Penn State-Harrisburg', 'PA', 'United East Conference', {  }),
  A('Pepperdine', 'CA', 'West Coast Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation' }),
  A('Piedmont', 'GA', 'Collegiate Conference of the South', {  }),
  A('Pitt-Bradford', 'PA', 'Allegheny Mountain Collegiate Conference', {  }),
  A('Pitt-Greensburg', 'PA', 'Allegheny Mountain Collegiate Conference', {  }),
  A('Pitt-Johnstown', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Pittsburgh', 'PA', 'Atlantic Coast Conference', {  }),
  A('Plattsburgh', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Plymouth State', 'NH', 'Little East Conference', {  }),
  A('Pomona-Pitzer', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Portland State', 'OR', 'Big Sky Conference', {  }),
  A('Potsdam', 'NY', 'State University of New York Athletic Conference', {  }),
  A('Prairie View A&M', 'TX', 'Southwestern Athletic Conference', {  }),
  A('Pratt Institute', 'NY', 'Atlantic East Conference', {  }),
  A('Presbyterian', 'SC', 'Big South Conference', { 'Football': 'Pioneer Football League' }),
  A('Princeton', 'NJ', 'Ivy League', { 'Ice Hockey': 'ECAC Hockey', 'Volleyball (men)': 'Eastern Intercollegiate Volleyball Association' }),
  A('Principia', 'IL', 'St. Louis Intercollegiate Athletic Conference', {  }),
  A('Providence', 'RI', 'Big East Conference', { 'Ice Hockey': 'Hockey East' }),
  A('Puget Sound', 'WA', 'Northwest Conference', {  }),
  A('Purdue', 'IN', 'Big Ten Conference', {  }),
  A('Purdue Fort Wayne', 'IN', 'Horizon League', { 'Volleyball (men)': 'Midwestern Intercollegiate Volleyball Association' }),
  A('Queens (NC)', 'NC', 'ASUN Conference', { 'Volleyball (men)': 'Midwestern Intercollegiate Volleyball Association' }),
  A('Quincy', 'IL', 'Great Lakes Valley Conference', {  }),
  A('Quinnipiac', 'CT', 'Metro Atlantic Athletic Conference', { 'Ice Hockey': 'ECAC Hockey' }),
  A('Radford', 'VA', 'Big South Conference', {  }),
  A('Ramapo', 'NJ', 'New Jersey Athletic Conference', {  }),
  A('Randolph', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Randolph-Macon', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Redlands', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Regent', 'VA', 'Coast to Coast Athletic Conference', {  }),
  A('Regis', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('Regis (MA)', 'MA', 'Great Northeast Athletic Conference', {  }),
  A('Rhode Island', 'RI', 'Atlantic 10 Conference', { 'Football': 'CAA Football' }),
  A('Rhode Island College', 'RI', 'Little East Conference', {  }),
  A('Rhodes', 'TN', 'Southern Athletic Association', {  }),
  A('Rice', 'TX', 'American Conference', {  }),
  A('Richmond', 'VA', 'Atlantic 10 Conference', {  }),
  A('Rider', 'NJ', 'Metro Atlantic Athletic Conference', {  }),
  A('Ripon', 'WI', 'Midwest Conference', {  }),
  A('RIT', 'NY', 'Liberty League', { 'Ice Hockey': 'Atlantic Hockey America' }),
  A('Rivier', 'NH', 'Great Northeast Athletic Conference', {  }),
  A('Roanoke', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Robert Morris', 'PA', 'Horizon League', { 'Ice Hockey': 'Atlantic Hockey America' }),
  A('Rochester', 'NY', 'University Athletic Association', {  }),
  A('Rockford', 'IL', 'Northern Athletics Collegiate Conference', {  }),
  A('Rockhurst', 'MO', 'Great Lakes Valley Conference', {  }),
  A('Roger Williams', 'RI', 'Conference of New England', {  }),
  A('Rollins', 'FL', 'Sunshine State Conference', {  }),
  A('Rose-Hulman', 'IN', 'Heartland Collegiate Athletic Conference', {  }),
  A('Rowan', 'NJ', 'New Jersey Athletic Conference', {  }),
  A('RPI', 'NY', 'Liberty League', { 'Ice Hockey': 'ECAC Hockey' }),
  A('Russell Sage', 'NY', 'Empire 8 Conference', {  }),
  A('Rutgers', 'NJ', 'Big Ten Conference', {  }),
  A('Rutgers-Camden', 'NJ', 'New Jersey Athletic Conference', {  }),
  A('Rutgers-Newark', 'NJ', 'New Jersey Athletic Conference', {  }),
  A('Sacramento State', 'CA', 'Big West Conference', {  }),
  A('Sacred Heart', 'CT', 'Metro Atlantic Athletic Conference', { 'Football': 'CAA Football', 'Ice Hockey': 'Atlantic Hockey America', 'Volleyball (men)': 'Eastern Intercollegiate Volleyball Association', 'Wrestling': 'Eastern Intercollegiate Wrestling Association' }),
  A('Saint Elizabeth', 'NJ', 'Atlantic East Conference', {  }),
  A('Saint Joseph\'s', 'PA', 'Atlantic 10 Conference', {  }),
  A('Saint Joseph\'s (CT)', 'CT', 'Great Northeast Athletic Conference', {  }),
  A('Saint Leo', 'FL', 'Sunshine State Conference', {  }),
  A('Saint Louis', 'MO', 'Atlantic 10 Conference', {  }),
  A('Saint Mary\'s (MI)', 'MI', 'Michigan Intercollegiate Athletic Association', {  }),
  A('Saint Mary\'s (MN)', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('Saint Peter\'s', 'NJ', 'Metro Atlantic Athletic Conference', {  }),
  A('Saint Vincent', 'PA', 'Presidents\' Athletic Conference', {  }),
  A('Salem State', 'MA', 'Massachusetts State Collegiate Athletic Conference', {  }),
  A('Salisbury', 'MD', 'Coast to Coast Athletic Conference', {  }),
  A('Salve Regina', 'RI', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('Samford', 'AL', 'Southern Conference', {  }),
  A('San Diego', 'CA', 'West Coast Conference', { 'Football': 'Pioneer Football League' }),
  A('San Jose State', 'CA', 'Mountain West Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation' }),
  A('Sarah Lawrence', 'NY', 'Skyline Conference', {  }),
  A('Schreiner', 'TX', 'American Southwest Conference', {  }),
  A('Scranton', 'PA', 'Landmark Conference', {  }),
  A('Seton Hall', 'NJ', 'Big East Conference', {  }),
  A('Seton Hill', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Sewanee', 'TN', 'Southern Athletic Association', {  }),
  A('Shaw', 'NC', 'Central Intercollegiate Athletic Association', {  }),
  A('Shenandoah', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Shepherd', 'WV', 'Pennsylvania State Athletic Conference', {  }),
  A('Shippensburg', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Siena', 'NY', 'Metro Atlantic Athletic Conference', {  }),
  A('Simpson', 'IA', 'American Rivers Conference', {  }),
  A('Sioux Falls', 'SD', 'Northern Sun Intercollegiate Conference', {  }),
  A('SIU Edwardsville', 'IL', 'Ohio Valley Conference', {  }),
  A('Skidmore', 'NY', 'Liberty League', {  }),
  A('Slippery Rock', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('Smith', 'MA', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('SMU', 'TX', 'Atlantic Coast Conference', {  }),
  A('South Alabama', 'AL', 'Sun Belt Conference', {  }),
  A('South Carolina', 'SC', 'Southeastern Conference', {  }),
  A('South Carolina State', 'SC', 'Mid-Eastern Athletic Conference', {  }),
  A('South Dakota', 'SD', 'Summit League', { 'Football': 'Missouri Valley Football Conference' }),
  A('South Dakota Mines', 'SD', 'Rocky Mountain Athletic Conference', {  }),
  A('South Dakota State', 'SD', 'Summit League', { 'Football': 'Missouri Valley Football Conference', 'Wrestling': 'Big 12 Conference' }),
  A('South Florida', 'FL', 'American Conference', {  }),
  A('Southeast Missouri', 'MO', 'Ohio Valley Conference', {  }),
  A('Southeastern Louisiana', 'LA', 'Southland Conference', {  }),
  A('Southern', 'LA', 'Southwestern Athletic Conference', {  }),
  A('Southern Illinois', 'IL', 'Missouri Valley Conference', { 'Football': 'Missouri Valley Football Conference' }),
  A('Southern Indiana', 'IN', 'Ohio Valley Conference', {  }),
  A('Southern Maine', 'ME', 'Little East Conference', {  }),
  A('Southern Miss', 'MS', 'Sun Belt Conference', {  }),
  A('Southern Utah', 'UT', 'Big Sky Conference', {  }),
  A('Southern Virginia', 'VA', 'USA South Athletic Conference', {  }),
  A('Southwest Baptist', 'MO', 'Great Lakes Valley Conference', {  }),
  A('Southwest Minnesota State', 'MN', 'Northern Sun Intercollegiate Conference', {  }),
  A('Southwestern (TX)', 'TX', 'Southern Athletic Association', {  }),
  A('Spalding', 'KY', 'St. Louis Intercollegiate Athletic Conference', {  }),
  A('Springfield', 'MA', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('St. Bonaventure', 'NY', 'Atlantic 10 Conference', {  }),
  A('St. Catherine', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('St. Cloud State', 'MN', 'Northern Sun Intercollegiate Conference', { 'Ice Hockey': 'Western Collegiate Hockey Association' }),
  A('St. Edward\'s', 'TX', 'Lone Star Conference', {  }),
  A('St. John Fisher', 'NY', 'Empire 8 Conference', {  }),
  A('St. John\'s', 'NY', 'Big East Conference', {  }),
  A('St. Joseph\'s-Brooklyn', 'NY', 'Skyline Conference', {  }),
  A('St. Joseph\'s-Long Island', 'NY', 'Skyline Conference', {  }),
  A('St. Lawrence', 'NY', 'Liberty League', { 'Ice Hockey': 'ECAC Hockey' }),
  A('St. Mary\'s', 'TX', 'Lone Star Conference', {  }),
  A('St. Mary\'s (MD)', 'MD', 'United East Conference', {  }),
  A('St. Norbert', 'WI', 'Northern Athletics Collegiate Conference', {  }),
  A('St. Olaf', 'MN', 'Minnesota Intercollegiate Athletic Conference', {  }),
  A('St. Scholastica', 'MN', 'Upper Midwest Athletic Conference', {  }),
  A('St. Thomas', 'MN', 'Summit League', { 'Football': 'Pioneer Football League', 'Ice Hockey': 'Western Collegiate Hockey Association' }),
  A('St. Thomas (TX)', 'TX', 'Southern Collegiate Athletic Conference', {  }),
  A('Stanford', 'CA', 'Atlantic Coast Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation', 'Water Polo': 'Mountain Pacific Sports Federation' }),
  A('Stephen F. Austin', 'TX', 'Southland Conference', {  }),
  A('Stetson', 'FL', 'ASUN Conference', { 'Football': 'Pioneer Football League' }),
  A('Stevenson', 'MD', 'MAC Freedom', {  }),
  A('Stockton', 'NJ', 'New Jersey Athletic Conference', {  }),
  A('Stonehill', 'MA', 'Northeast Conference', {  }),
  A('Stony Brook', 'NY', 'Coastal Athletic Association', {  }),
  A('Suffolk', 'MA', 'Conference of New England', {  }),
  A('Sul Ross State', 'TX', 'Lone Star Conference', {  }),
  A('SUNY Farmingdale', 'NY', 'Skyline Conference', {  }),
  A('SUNY Maritime', 'NY', 'Skyline Conference', {  }),
  A('SUNY New Paltz', 'NY', 'New Jersey Athletic Conference', {  }),
  A('SUNY Old Westbury', 'NY', 'Skyline Conference', {  }),
  A('SUNY Poly', 'NY', 'Empire 8 Conference', {  }),
  A('SUNY Purchase', 'NY', 'Skyline Conference', {  }),
  A('Susquehanna', 'PA', 'Centennial Conference', {  }),
  A('Swarthmore', 'PA', 'Centennial Conference', {  }),
  A('Sweet Briar', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Syracuse', 'NY', 'Atlantic Coast Conference', { 'Ice Hockey': 'Atlantic Hockey America' }),
  A('Tampa', 'FL', 'Sunshine State Conference', {  }),
  A('Tarleton State', 'TX', 'United Athletic Conference', {  }),
  A('TCNJ', 'NJ', 'New Jersey Athletic Conference', {  }),
  A('TCU', 'TX', 'Big 12 Conference', {  }),
  A('Temple', 'PA', 'American Conference', {  }),
  A('Tennessee', 'TN', 'Southeastern Conference', {  }),
  A('Tennessee State', 'TN', 'Ohio Valley Conference', {  }),
  A('Tennessee Tech', 'TN', 'Southern Conference', {  }),
  A('Texas', 'TX', 'Southeastern Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation' }),
  A('Texas A&M', 'TX', 'Southeastern Conference', {  }),
  A('Texas A&M International', 'TX', 'Lone Star Conference', {  }),
  A('Texas A&M-Corpus Christi', 'TX', 'Southland Conference', {  }),
  A('Texas A&M-Kingsville', 'TX', 'Lone Star Conference', {  }),
  A('Texas Lutheran', 'TX', 'Southern Collegiate Athletic Conference', {  }),
  A('Texas Southern', 'TX', 'Southwestern Athletic Conference', {  }),
  A('Texas State', 'TX', 'Sun Belt Conference', {  }),
  A('Texas Tech', 'TX', 'Big 12 Conference', {  }),
  A('Texas Woman\'s', 'TX', 'Lone Star Conference', {  }),
  A('Thiel', 'PA', 'Presidents\' Athletic Conference', {  }),
  A('Thomas', 'ME', 'North Atlantic Conference', {  }),
  A('Toledo', 'OH', 'Mid-American Conference', {  }),
  A('Towson', 'MD', 'Coastal Athletic Association', {  }),
  A('Transylvania', 'KY', 'Heartland Collegiate Athletic Conference', {  }),
  A('Trevecca Nazarene', 'TN', 'Gulf South Conference', {  }),
  A('Trine', 'IN', 'Heartland Collegiate Athletic Conference', {  }),
  A('Trinity (CT)', 'CT', 'New England Small College Athletic Conference', {  }),
  A('Trinity (TX)', 'TX', 'Southern Athletic Association', {  }),
  A('Troy', 'AL', 'Sun Belt Conference', {  }),
  A('Truman', 'MO', 'Great Lakes Valley Conference', {  }),
  A('Tufts', 'MA', 'New England Small College Athletic Conference', {  }),
  A('Tulane', 'LA', 'American Conference', {  }),
  A('Tulsa', 'OK', 'American Conference', {  }),
  A('Tusculum', 'TN', 'South Atlantic Conference', {  }),
  A('UAB', 'AL', 'American Conference', {  }),
  A('UAH', 'AL', 'Gulf South Conference', {  }),
  A('UC Irvine', 'CA', 'Big West Conference', {  }),
  A('UC Riverside', 'CA', 'Big West Conference', {  }),
  A('UC San Diego', 'CA', 'Big West Conference', {  }),
  A('UC Santa Barbara', 'CA', 'Big West Conference', {  }),
  A('UC Santa Cruz', 'CA', 'Coast to Coast Athletic Conference', {  }),
  A('UCCS', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('UCF', 'FL', 'Big 12 Conference', {  }),
  A('UCLA', 'CA', 'Big Ten Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation', 'Water Polo': 'Mountain Pacific Sports Federation' }),
  A('UConn', 'CT', 'Big East Conference', { 'Ice Hockey': 'Hockey East' }),
  A('UIC', 'IL', 'Missouri Valley Conference', {  }),
  A('UIndy', 'IN', 'Great Lakes Valley Conference', {  }),
  A('UIS', 'IL', 'Great Lakes Valley Conference', {  }),
  A('UMass', 'MA', 'Atlantic 10 Conference', { 'Ice Hockey': 'Hockey East' }),
  A('UMass Dartmouth', 'MA', 'Little East Conference', {  }),
  A('UMass Lowell', 'MA', 'America East Conference', { 'Ice Hockey': 'Hockey East' }),
  A('UMBC', 'MD', 'America East Conference', {  }),
  A('UMHB', 'TX', 'American Southwest Conference', {  }),
  A('UMSL', 'MO', 'Great Lakes Valley Conference', {  }),
  A('UNC Asheville', 'NC', 'Big South Conference', {  }),
  A('UNC Greensboro', 'NC', 'Southern Conference', {  }),
  A('UNC Wilmington', 'NC', 'Coastal Athletic Association', {  }),
  A('Union', 'NY', 'Liberty League', { 'Ice Hockey': 'ECAC Hockey' }),
  A('Union (TN)', 'TN', 'Gulf South Conference', {  }),
  A('Upper Iowa', 'IA', 'Great Lakes Valley Conference', {  }),
  A('Ursinus', 'PA', 'Centennial Conference', {  }),
  A('USC', 'CA', 'Big Ten Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation', 'Water Polo': 'Mountain Pacific Sports Federation' }),
  A('USC Aiken', 'SC', 'Peach Belt Conference', {  }),
  A('USC Beaufort', 'SC', 'Peach Belt Conference', {  }),
  A('USC Upstate', 'SC', 'Big South Conference', {  }),
  A('UT Arlington', 'TX', 'United Athletic Conference', {  }),
  A('UT Dallas', 'TX', 'Lone Star Conference', {  }),
  A('UT Martin', 'TN', 'Ohio Valley Conference', {  }),
  A('UT Permian Basin', 'TX', 'Lone Star Conference', {  }),
  A('UT Tyler', 'TX', 'Lone Star Conference', {  }),
  A('Utah', 'UT', 'Big 12 Conference', {  }),
  A('Utah Tech', 'UT', 'Big Sky Conference', {  }),
  A('Utah Valley', 'UT', 'Big West Conference', { 'Wrestling': 'Big 12 Conference' }),
  A('Utica', 'NY', 'Empire 8 Conference', {  }),
  A('UTRGV', 'TX', 'Southland Conference', {  }),
  A('UTSA', 'TX', 'American Conference', {  }),
  A('UVA Wise', 'VA', 'South Atlantic Conference', {  }),
  A('UW-Eau Claire', 'WI', 'Wisconsin Intercollegiate Athletic Conference', {  }),
  A('UW-La Crosse', 'WI', 'Wisconsin Intercollegiate Athletic Conference', {  }),
  A('UW-Oshkosh', 'WI', 'Wisconsin Intercollegiate Athletic Conference', {  }),
  A('UW-Platteville', 'WI', 'Wisconsin Intercollegiate Athletic Conference', {  }),
  A('UW-River Falls', 'WI', 'Wisconsin Intercollegiate Athletic Conference', {  }),
  A('UW-Stevens Point', 'WI', 'Wisconsin Intercollegiate Athletic Conference', {  }),
  A('UW-Stout', 'WI', 'Wisconsin Intercollegiate Athletic Conference', {  }),
  A('UW-Superior', 'WI', 'Wisconsin Intercollegiate Athletic Conference', {  }),
  A('UW-Whitewater', 'WI', 'Wisconsin Intercollegiate Athletic Conference', {  }),
  A('Valdosta State', 'GA', 'Gulf South Conference', {  }),
  A('Valley Forge', 'PA', 'United East Conference', {  }),
  A('Valparaiso', 'IN', 'Missouri Valley Conference', { 'Football': 'Pioneer Football League' }),
  A('Vanderbilt', 'TN', 'Southeastern Conference', {  }),
  A('Vassar', 'NY', 'Liberty League', {  }),
  A('VCU', 'VA', 'Atlantic 10 Conference', {  }),
  A('Vermont', 'VT', 'America East Conference', { 'Ice Hockey': 'Hockey East' }),
  A('Villanova', 'PA', 'Big East Conference', {  }),
  A('Virginia', 'VA', 'Atlantic Coast Conference', {  }),
  A('Virginia State', 'VA', 'Central Intercollegiate Athletic Association', {  }),
  A('Virginia Tech', 'VA', 'Atlantic Coast Conference', {  }),
  A('Virginia Union', 'VA', 'Central Intercollegiate Athletic Association', {  }),
  A('Virginia Wesleyan', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('VMI', 'VA', 'Southern Conference', {  }),
  A('VSU Castleton', 'VT', 'Little East Conference', {  }),
  A('VSU Johnson', 'VT', 'North Atlantic Conference', {  }),
  A('VSU Lyndon', 'VT', 'North Atlantic Conference', {  }),
  A('Wabash', 'IN', 'North Coast Athletic Conference', {  }),
  A('Wagner', 'NY', 'Northeast Conference', {  }),
  A('Wake Forest', 'NC', 'Atlantic Coast Conference', {  }),
  A('Warren Wilson', 'NC', 'Coast to Coast Athletic Conference', {  }),
  A('Wartburg', 'IA', 'American Rivers Conference', {  }),
  A('Washington', 'WA', 'Big Ten Conference', { 'Volleyball (men)': 'Mountain Pacific Sports Federation' }),
  A('Washington (MO)', 'MO', 'University Athletic Association', {  }),
  A('Washington & Jefferson', 'PA', 'Presidents\' Athletic Conference', {  }),
  A('Washington and Lee', 'VA', 'Old Dominion Athletic Conference', {  }),
  A('Washington College', 'MD', 'Centennial Conference', {  }),
  A('Wayne State', 'NE', 'Northern Sun Intercollegiate Conference', {  }),
  A('Waynesburg', 'PA', 'Presidents\' Athletic Conference', {  }),
  A('Weber State', 'UT', 'Big Sky Conference', {  }),
  A('Webster', 'MO', 'St. Louis Intercollegiate Athletic Conference', {  }),
  A('Wellesley', 'MA', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('Wentworth', 'MA', 'Conference of New England', {  }),
  A('Wesleyan', 'CT', 'New England Small College Athletic Conference', {  }),
  A('Wesleyan (GA)', 'GA', 'Collegiate Conference of the South', {  }),
  A('West Alabama', 'AL', 'Gulf South Conference', {  }),
  A('West Chester', 'PA', 'Pennsylvania State Athletic Conference', {  }),
  A('West Florida', 'FL', 'ASUN Conference', {  }),
  A('West Georgia', 'GA', 'United Athletic Conference', {  }),
  A('West Texas A&M', 'TX', 'Lone Star Conference', {  }),
  A('West Virginia', 'WV', 'Big 12 Conference', {  }),
  A('Western Carolina', 'NC', 'Southern Conference', {  }),
  A('Western Colorado', 'CO', 'Rocky Mountain Athletic Conference', {  }),
  A('Western Connecticut', 'CT', 'Little East Conference', {  }),
  A('Western Illinois', 'IL', 'Ohio Valley Conference', {  }),
  A('Western Michigan', 'MI', 'Mid-American Conference', { 'Ice Hockey': 'National Collegiate Hockey Conference' }),
  A('Western New England', 'MA', 'Conference of New England', {  }),
  A('Western New Mexico', 'NM', 'Lone Star Conference', {  }),
  A('Westfield State', 'MA', 'Massachusetts State Collegiate Athletic Conference', {  }),
  A('Westminster', 'UT', 'Rocky Mountain Athletic Conference', {  }),
  A('Westminster (MO)', 'MO', 'St. Louis Intercollegiate Athletic Conference', {  }),
  A('Westminster (PA)', 'PA', 'Presidents\' Athletic Conference', {  }),
  A('Wheaton', 'IL', 'College Conference of Illinois and Wisconsin', {  }),
  A('Wheaton (MA)', 'MA', 'New England Women\'s and Men\'s Athletic Conference', {  }),
  A('Whitman', 'WA', 'Northwest Conference', {  }),
  A('Whittier', 'CA', 'Southern California Intercollegiate Athletic Conference', {  }),
  A('Whitworth', 'WA', 'Northwest Conference', {  }),
  A('Wichita State', 'KS', 'American Conference', {  }),
  A('Widener', 'PA', 'MAC Commonwealth', {  }),
  A('Wilkes', 'PA', 'MAC Freedom', {  }),
  A('Willamette', 'OR', 'Northwest Conference', {  }),
  A('William & Mary', 'VA', 'Coastal Athletic Association', {  }),
  A('William Jewell', 'MO', 'Great Lakes Valley Conference', {  }),
  A('William Paterson', 'NJ', 'New Jersey Athletic Conference', {  }),
  A('Williams', 'MA', 'New England Small College Athletic Conference', {  }),
  A('Wilmington', 'OH', 'Ohio Athletic Conference', {  }),
  A('Wilson (PA)', 'PA', 'United East Conference', {  }),
  A('Wingate', 'NC', 'South Atlantic Conference', {  }),
  A('Winona State', 'MN', 'Northern Sun Intercollegiate Conference', {  }),
  A('Winston-Salem State', 'NC', 'Central Intercollegiate Athletic Association', {  }),
  A('Winthrop', 'SC', 'Big South Conference', {  }),
  A('Wisconsin', 'WI', 'Big Ten Conference', { 'Ice Hockey': 'Western Collegiate Hockey Association' }),
  A('Wisconsin Lutheran', 'WI', 'Northern Athletics Collegiate Conference', {  }),
  A('Wittenberg', 'OH', 'North Coast Athletic Conference', {  }),
  A('Wofford', 'SC', 'Southern Conference', {  }),
  A('Wooster', 'OH', 'North Coast Athletic Conference', {  }),
  A('Worcester State', 'MA', 'Massachusetts State Collegiate Athletic Conference', {  }),
  A('WPI', 'MA', 'Liberty League', {  }),
  A('Wright State', 'OH', 'Horizon League', {  }),
  A('Wyoming', 'WY', 'Mountain West Conference', { 'Wrestling': 'Big 12 Conference' }),
  A('Xavier', 'OH', 'Big East Conference', {  }),
  A('Yale', 'CT', 'Ivy League', { 'Ice Hockey': 'ECAC Hockey' }),
  A('Yeshiva', 'NY', 'Skyline Conference', {  }),
  A('York College (NY)', 'NY', 'City University of New York Athletic Conference', {  }),
  A('Youngstown State', 'OH', 'Horizon League', { 'Football': 'Missouri Valley Football Conference' }),
]

const BY_NAME = new Map(SCHOOL_AFFILIATION.map((s) => [L(s.name), s]))

/**
 * ⚠️ SPORT NAMES ARRIVE IN AT LEAST THREE SPELLINGS AND A SUBSTRING TEST IS NOT
 * ENOUGH. My first version matched loosely with `includes`, and
 * "Volleyball (men)" resolved to the EIVA while "Men's Volleyball" — the SAME
 * championship — fell through to the Big Ten, because neither collapsed string
 * contains the other. That is a wrong conference returned silently, which is
 * the worst possible shape for this function: it looks like an answer.
 *
 * So a sport is reduced to a SORTED TOKEN SET. "Men's Volleyball",
 * "Volleyball (men)" and "Volleyball - Men" all become `men|volleyball`.
 */
const SPORT_STOP = new Set(['the', 'and', 'field', 'division', 'conference', 'championship'])
const SPORT_SYNONYM = new Map([
  ['mens', 'men'], ['womens', 'women'], ['boys', 'men'], ['girls', 'women'],
  ['ice', 'icehockey'], ['hockey', 'icehockey'],
])
function sportKey(v) {
  const toks = String(v ?? '')
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((t) => SPORT_SYNONYM.get(t) || t)
    .filter((t) => !SPORT_STOP.has(t))
  return [...new Set(toks)].sort().join('|')
}

/**
 * WHICH CONFERENCE CROWNS THIS SCHOOL IN THIS SPORT?
 *
 * ⚠️ RETURNS null WHEN THE SCHOOL IS UNSOURCED — never a guess. `null` means
 * "we have not recorded this school yet", which is a different statement from
 * "this school has no conference", and collapsing them would make an unsourced
 * school look independent.
 */
export function conferenceFor(school, sport = '') {
  const s = BY_NAME.get(L(school))
  if (!s) return null
  if (!sport) return s.primary
  /* An exact sport match wins; then a loose one, so "Men's Volleyball" and
   * "Volleyball (men)" resolve to the same exception rather than silently
   * falling through to the primary conference. */
  const want = sportKey(sport)
  const keys = Object.keys(s.except)
  const exact = keys.find((k) => sportKey(k) === want)
  if (exact) return s.except[exact]
  /* A gendered query against an ungendered exception, or the reverse:
   * "Men's Lacrosse" must still find a `Lacrosse` departure. Compared as
   * SUBSETS of the token set, never as substrings of the string. */
  const wantTok = new Set(want.split('|').filter(Boolean))
  const partial = keys.find((k) => {
    const kt = new Set(sportKey(k).split('|').filter(Boolean))
    const smaller = kt.size <= wantTok.size ? kt : wantTok
    const larger = kt.size <= wantTok.size ? wantTok : kt
    return smaller.size > 0 && [...smaller].every((t) => larger.has(t))
  })
  return partial ? s.except[partial] : s.primary
}

/**
 * Could this school win this conference's championship in this sport?
 *
 * Three answers, and the third is the important one:
 *   true   — yes, this conference crowns this school in this sport
 *   false  — no, another body does (or nobody does)
 *   null   — NOT SOURCED YET. Not the same as "no".
 *
 * ⚠️ THE null CASE IS WHAT KEEPS THE CHECK USABLE. With ~1,100 schools to
 * source, treating unsourced as ineligible would produce a wall of false
 * negatives on day one and teach everybody to ignore the warning — which is
 * exactly how a real eligibility error would then get through.
 */
export function canWin(school, sport, conference) {
  const actual = conferenceFor(school, sport)
  if (actual === null) return null
  return L(actual) === L(conference)
}

/**
 * Every school we have sourced that this conference crowns in this sport.
 *
 * ⚠️ ASKED FOR A SPORT, THIS ONLY RETURNS SCHOOLS WITH AN EXPLICIT AFFILIATION
 * IN IT — and that is a correction. The first version fell back to each
 * school's PRIMARY conference for any sport not listed as a departure, which
 * quietly asserted that every school plays every sport: it returned Wisconsin
 * and Minnesota as Big Ten LACROSSE schools, which neither plays. Claiming a
 * school competes in a sport it does not field is the same error as a phantom
 * championship, and it would put a rep on the phone about a team that does not
 * exist.
 *
 * With no sport it returns the conference's primary members, which is a
 * different and safe question ("who is in this league").
 */
/**
 * ⚠️ THE CONFERENCES WHOSE ROSTER IS **COMPLETE**.
 *
 * Brian: "every conference must show all the teams — you cant have a big south
 * showing one school."
 *
 * He is right, and the cause was structural. A conference we have NOT sourced
 * still shows any school that happens to appear as a DEPARTURE — Presbyterian
 * turned up in the Pioneer Football roster, so Big South rendered "1 team".
 * That is worse than showing nothing: a partial roster presented as a roster is
 * a confident lie about a conference with nine members.
 *
 * So the UI must be able to tell "we know all of them" from "we happen to know
 * one of them", and this is what it reads. GENERATED — it is exactly the set of
 * conferences a full roster was collected for.
 */
export const SOURCED_CONFERENCES = new Set([
  'ASUN Conference',
  'Allegheny Mountain Collegiate Conference',
  'America East Conference',
  'American Conference',
  'American Rivers Conference',
  'American Southwest Conference',
  'Atlantic 10 Conference',
  'Atlantic Coast Conference',
  'Atlantic East Conference',
  'Big 12 Conference',
  'Big East Conference',
  'Big Sky Conference',
  'Big South Conference',
  'Big Ten Conference',
  'Big West Conference',
  'Centennial Conference',
  'Central Intercollegiate Athletic Association',
  'City University of New York Athletic Conference',
  'Coast to Coast Athletic Conference',
  'Coastal Athletic Association',
  'College Conference of Illinois and Wisconsin',
  'Collegiate Conference of the South',
  'Conference of New England',
  'Empire 8 Conference',
  'Great Lakes Valley Conference',
  'Great Northeast Athletic Conference',
  'Gulf South Conference',
  'Heartland Collegiate Athletic Conference',
  'Horizon League',
  'Ivy League',
  'Landmark Conference',
  'Liberty League',
  'Little East Conference',
  'Lone Star Conference',
  'MAC Commonwealth',
  'MAC Freedom',
  'Massachusetts State Collegiate Athletic Conference',
  'Metro Atlantic Athletic Conference',
  'Michigan Intercollegiate Athletic Association',
  'Mid-American Conference',
  'Mid-Eastern Athletic Conference',
  'Midwest Conference',
  'Minnesota Intercollegiate Athletic Conference',
  'Missouri Valley Conference',
  'New England Small College Athletic Conference',
  'New England Women\'s and Men\'s Athletic Conference',
  'New Jersey Athletic Conference',
  'North Atlantic Conference',
  'North Coast Athletic Conference',
  'Northeast Conference',
  'Northern Athletics Collegiate Conference',
  'Northern Sun Intercollegiate Conference',
  'Northwest Conference',
  'Ohio Athletic Conference',
  'Ohio Valley Conference',
  'Old Dominion Athletic Conference',
  'Patriot League',
  'Peach Belt Conference',
  'Pennsylvania State Athletic Conference',
  'Presidents\' Athletic Conference',
  'Rocky Mountain Athletic Conference',
  'Skyline Conference',
  'South Atlantic Conference',
  'Southeastern Conference',
  'Southern Athletic Association',
  'Southern California Intercollegiate Athletic Conference',
  'Southern Collegiate Athletic Conference',
  'Southern Conference',
  'Southland Conference',
  'Southwestern Athletic Conference',
  'St. Louis Intercollegiate Athletic Conference',
  'State University of New York Athletic Conference',
  'Summit League',
  'Sun Belt Conference',
  'Sunshine State Conference',
  'USA South Athletic Conference',
  'United Athletic Conference',
  'United East Conference',
  'University Athletic Association',
  'Upper Midwest Athletic Conference',
  'Wisconsin Intercollegiate Athletic Conference',
])

/** Is this conference's member list complete, or only incidental departures? */
export const isRosterComplete = (conference) =>
  SOURCED_CONFERENCES.has(String(conference ?? '').trim())
  || [...SOURCED_CONFERENCES].some((c) => c.toLowerCase() === String(conference ?? '').trim().toLowerCase())

export const DEPARTURE_SPORTS = [...new Set(
  SCHOOL_AFFILIATION.flatMap((x) => Object.keys(x.except)),
)].sort()

export function schoolsIn(conference, sport = '') {
  if (!sport) {
    const primary = SCHOOL_AFFILIATION
      .filter((s) => L(s.primary) === L(conference))
      .map((s) => ({ name: s.name, state: s.state }))
    if (primary.length) return primary
    /* ⚠️ A SPORT-SPECIFIC BODY HAS NO PRIMARY MEMBERS AT ALL — nobody's home
     * conference is Hockey East or the EIWA; every one of its schools is there
     * for one sport and elsewhere for the rest. Returning [] for those made
     * their cards read as empty when they have twelve schools each. So when
     * nothing calls this body home, its members ARE its departures. */
    const seen = new Set()
    const out = []
    for (const s of SCHOOL_AFFILIATION) {
      for (const [sportKey, body] of Object.entries(s.except)) {
        if (L(body) !== L(conference) || seen.has(s.name)) continue
        seen.add(s.name)
        out.push({ name: s.name, state: s.state, only: sportKey })
      }
    }
    return out
  }
  const want = sportKey(sport)
  return SCHOOL_AFFILIATION
    .filter((s) => Object.keys(s.except).some((k) => {
      const kt = new Set(sportKey(k).split('|').filter(Boolean))
      const wt = new Set(want.split('|').filter(Boolean))
      const smaller = kt.size <= wt.size ? kt : wt
      const larger = kt.size <= wt.size ? wt : kt
      return smaller.size > 0 && [...smaller].every((t) => larger.has(t))
    }))
    .filter((s) => L(conferenceFor(s.name, sport)) === L(conference))
    .map((s) => ({ name: s.name, state: s.state }))
}

/**
 * The sports where this school departs from its primary conference — the rows
 * that a school-level model gets wrong, listed so they can be checked.
 */
export function departuresFor(school) {
  const s = BY_NAME.get(L(school))
  if (!s) return []
  return Object.entries(s.except).map(([sport, conference]) => ({ sport, conference }))
}

/** How much of the association is actually sourced. Reported, never implied. */
export function coverage() {
  const withExceptions = SCHOOL_AFFILIATION.filter((s) => Object.keys(s.except).length)
  return {
    schools: SCHOOL_AFFILIATION.length,
    withDepartures: withExceptions.length,
    departures: withExceptions.reduce((n, s) => n + Object.keys(s.except).length, 0),
  }
}
